import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import InConstruction from "./InConstrution";

// Build parking from docs using labels mapping (supports place:"A3" or numeric "23")
const buildParkingFromDocs = (querySnapshot, labels = [], total = null) => {
  const size = total || (Array.isArray(labels) ? labels.length : 0) || 91;
  const arr = Array(size).fill(null);

  querySnapshot.forEach(docSnap => {
    const data = docSnap.data() || {};
    const placeRaw = data.place;
    const bus = data.bus ?? null;

    let idx = null;

    // if place is exactly a label and labels provided -> map by label
    if (typeof placeRaw === "string") {
      const trimmed = placeRaw.trim();
      if (Array.isArray(labels) && labels.length) {
        const labelIdx = labels.indexOf(trimmed);
        if (labelIdx >= 0) idx = labelIdx;
      }
      // fallback: pure digits or trailing digits -> numeric place
      if (idx === null) {
        const m = trimmed.match(/^(\d+)$/) || trimmed.match(/(\d+)$/);
        if (m) {
          const placeNum = Number(m[1]);
          if (Number.isInteger(placeNum) && placeNum >= 1 && placeNum <= size) idx = placeNum - 1;
        }
      }
    } else if (typeof placeRaw === "number") {
      const placeNum = placeRaw;
      if (Number.isInteger(placeNum) && placeNum >= 1 && placeNum <= size) idx = placeNum - 1;
    }

    // legacy fallbacks by id/pos/index fields
    if (idx === null) {
      if (data.index !== undefined && !Number.isNaN(Number(data.index))) idx = Number(data.index);
      else if (data.pos !== undefined && !Number.isNaN(Number(data.pos))) idx = Number(data.pos);
      else if (!Number.isNaN(parseInt(docSnap.id, 10))) idx = parseInt(docSnap.id, 10) - 1;
    }

    if (idx !== null && idx >= 0 && idx < size) {
      arr[idx] = bus;
    }
  });

  return arr;
};

export default function Bus_Park_Print() {
  const navigate = useNavigate();

  // same columns spec as main Bus_Park
  const columnsSpec = [
    { letter: "A", count: 5, height: 195 },
    { letter: "B", count: 5, height: 195 },
    { letter: "C", count: 5, height: 195 },
    { letter: "D", count: 5, height: 195 },
    { letter: "E", count: 7, height: 140 },
    { letter: "F", count: 7, height: 140 },
    { letter: "G", count: 7, height: 140 },
    { letter: "H", count: 7, height: 140 },
    { letter: "I", count: 7, height: 140 },
    { letter: "J", count: 7, height: 140 },
    { letter: "K", count: 7, height: 140 },
    { letter: "L", count: 10, height: 140, rotated: true },
    { letter: "M", count: 10, height: 140, rotated: true },
  ];

  // build labels A1..Mx then top row N1..N16 (match Bus_Park.jsx)
  const labels = useMemo(() => {
    const arr = [];
    columnsSpec.forEach(col => {
      for (let i = 1; i <= col.count; i++) arr.push(`${col.letter}${i}`);
    });
    for (let i = 1; i <= 16; i++) arr.push(`N${i}`);
    return arr;
  }, [columnsSpec]);

  const labelToIndex = useMemo(() => {
    const m = {};
    labels.forEach((l, i) => { m[l] = i; });
    return m;
  }, [labels]);

  // parking sized to labels length
  const [parking, setParking] = useState(Array(labels.length).fill(null));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "BusParkPlaces"));
        setParking(buildParkingFromDocs(querySnapshot, labels, labels.length));
      } catch (err) {
        console.error("Failed to load BusParkPlaces:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [labels]);

  const renderNumberSpot = (index, width, height, rotate = 0, topOffset = 0) => {
    if (index === undefined || index === null || index < 0 || index >= labels.length) {
      // placeholder empty box for missing index
      return (
        <Box
          key={String(index)}
          sx={{
            width,
            height,
            bgcolor: "#fff",
            border: "3px solid #000",
            borderRadius: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            transform: `rotate(${rotate}deg)`,
            mt: topOffset,
            boxSizing: "border-box",
            overflow: "visible",
          }}
        />
      );
    }

    return (
      <Box
        key={index}
        sx={{
          width,
          height,
          bgcolor: "#fff",
          border: "3px solid #000",
          borderRadius: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          margin: 0,
          transform: `rotate(${rotate}deg)`,
          mt: topOffset,
          boxSizing: "border-box",
          overflow: "visible",
        }}
      >
        <Typography
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            color: "#9e9e9e",
            fontSize: 18,
            fontWeight: 500,
            userSelect: "none"
          }}
        >
          {labels[index] ?? String(index + 1)}
        </Typography>

        {parking[index] && (
          <Typography
            sx={{
              color: "#000",
              fontSize: 18,
              fontWeight: "bold",
              textAlign: "center",
              width: "100%",
              letterSpacing: 1,
              userSelect: "none",
              mt: 4,
            }}
          >
            {parking[index]}
          </Typography>
        )}
      </Box>
    );
  };

  // helper to render by label
  const renderByLabel = (label, w, h, rotate = 0, topOffset = 0) => {
    const idx = labelToIndex[label];
    return <Box key={label} sx={{ mx: 0 }}>{renderNumberSpot(idx, w, h, rotate, topOffset)}</Box>;
  };

  return (
    <>
      <InConstruction />
      <Box sx={{ width: "100vw", minHeight: "100vh", bgcolor: "#fff", p: { xs: 2, md: 4 } }}>
        <Box sx={{ display: "flex", justifyContent: "left", alignItems: "center", mb: 2 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: "bold" }}>
            Bus Park - Print View
          </Typography>
          <IconButton
            sx={{
              ml: 1,
              color: "#fff",
              bgcolor: "#444",
              "&:hover": { bgcolor: "#666" },
              boxShadow: 2
            }}
            onClick={() => navigate(-1)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* TOP ROW: N1..N16 (match Bus_Park.jsx layout). Use same size as row E (50x65). */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            gap: 0.5,
            mb: 1,
            justifyContent: { xs: "flex-start", sm: "center" },
            px: { xs: 0, sm: 4 },
            overflow: "visible",     // allow lifted items to remain visible
            position: "relative",
            zIndex: 20,
          }}
        >
          {(() => {
            const spec = [
              "N1","N2","N3","N4","N5","N6",
              ["N7","N8"], // stacked rotated pair
              ["N9","N10"], // stacked rotated pair
              "N11","N12","N13","N14","N15","N16"
            ];

            return spec.map((item, idx) => {
              if (Array.isArray(item)) {
                return (
                  <Box
                    key={`group-${idx}`}
                    sx={{
                      mx: 0.3,
                      mt: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                      alignItems: "center",
                      overflow: "visible",     // ensure stacked pair isn't clipped
                      position: "relative",
                      // ensure 5px vertical space between stacked rotated rectangles
                      "& > *:not(:first-child)": { mt: "5px" }
                    }}
                  >
                    {item.map((label, i2) => {
                      // lift N7..N10 visually using `position: relative; top: -50px` instead of negative mt
                      const liftLabels = ["N7", "N8", "N9", "N10"];
                      const lift = liftLabels.includes(label) ? -50 : 0;
                      return (
                        <Box
                          key={label}
                          sx={{
                            overflow: "visible",
                            position: lift ? "relative" : "static",
                            top: lift ? `${lift}px` : undefined,
                          }}
                        >
                          {renderByLabel(label, 50, 65, 90, 0)}
                        </Box>
                      );
                    })}
                  </Box>
                );
              }
              return (
                <Box key={item} sx={{ mx: 0.25, overflow: "visible" }}>
                  {renderByLabel(item, 50, 65)}
                </Box>
              );
            });
          })()}
        </Box>

        <Box
          sx={{
            mt: 1,
            width: "100%",
            display: "flex",
            flexDirection: "row",
            gap: 0,
            justifyContent: { xs: "flex-start", sm: "center" },
            alignItems: { xs: "flex-start", sm: "center" },
            flexWrap: "nowrap",
            px: { xs: 0, sm: 4 },
          }}
        >
          {/* Columns A..D — smaller to match E..K sizing */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {[5,4,3,2,1].map(i => renderByLabel(`A${i}`, 50, 90))}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {[5,4,3,2,1].map(i => renderByLabel(`B${i}`, 50, 90))}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {[5,4,3,2,1].map(i => renderByLabel(`C${i}`, 50, 90))}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {[5,4,3,2,1].map(i => renderByLabel(`D${i}`, 50, 90))}
          </Box>

          {/* Columns E..K (7 columns x 7 rows) - keep compact size */}
          <Box sx={{ display: "flex", flexDirection: "row", gap: 0 }}>
            {["E","F","G","H","I","J","K"].map(letter => (
              <Box key={letter} sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {[7,6,5,4,3,2,1].map(i => renderByLabel(`${letter}${i}`, 50, 65))}
              </Box>
            ))}
          </Box>

          {/* Column separator */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", px: 2 }}>
            <Box sx={{ width: 15, height: 495, bgcolor: "#fff", border: "2px solid #000" }} />
          </Box>

          {/* Rotated columns L and M (10 each) */}
          <Box sx={{ display: "flex", flexDirection: "row", mt: 4, ml: 1.5, gap: "3px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", mt: -6.3, mr: 1.5, gap: 0 }}>
              {[10,9,8,7,6,5,4,3,2,1].map(i => renderByLabel(`L${i}`, 50, 65, 75))}
            </Box>
            {/* Column M lifted higher (more to the top) and separated from L by 5px */}
            <Box sx={{ display: "flex", flexDirection: "column", mt: -11, gap: 0 }}>
              {[10,9,8,7,6,5,4,3,2,1].map((i, idx) => renderByLabel(`M${i}`, 50, 65, 75, idx === 0 ? 2.5 : 0))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>{new Date().toLocaleDateString()}</Typography>
        </Box>
      </Box>
    </>
  );
}